import type {PageObject, PageObjectWithLayout, PrintEditionData, ProductData} from '../../types/types.ts';
import type {ContentAdapter} from '../adapters/content-adapter.ts';
import {LoadResourceError} from '../lib/errors.ts';
import {createArticleFromLayout} from '../lib/layout.ts';
import type {Store} from '../lib/store.ts';
import type {ReaderListing} from '../pages/reader/reader-listing.ts';
import {loadEditionLayouts} from '../services/content.ts';
import {Article} from './article.ts';
import {ReaderEdition} from './reader-edition.ts';

export class PrintEdition extends ReaderEdition {
	editionData: PrintEditionData;
	public readonly editionType = 'print-edition';
	public readonly productType = 'print';
	articleIdPrefix = '';

	constructor(editionData: PrintEditionData, productData: ProductData, adapter: ContentAdapter) {
		super(adapter);
		this.editionData = editionData;
		this.id = editionData._id;
		this.title = editionData.title;
		this.publishDate = editionData.publishDate;
		this.productId = productData.id;
		this.productName = productData.attributes.name;
		this.productTitle = productData.attributes.title;
		this.label = productData.attributes.title;
	}

	getSection(listing: ReaderListing) {
		// Assume article groups are notices within a section
		if (listing.item instanceof Article && listing.item.isArticleGroup) {
			return '';
		}

		return listing.item.section;
	}

	async loadFullArticle(
		_article: Article,
		_groupBy: string,
		_sortOrder: number | undefined,
		_store: Store
	): Promise<Article | null> {
		// Print editions have already loaded articles during the list loading
		return null;
	}

	async loadArticleList(store: Store) {
		await store.loadServerStatesForPrintEdition(this.id);

		const articleObjects = Object.values(this.editionData.objects).filter(
			({layoutPreview, itemId}) => layoutPreview && itemId
		);
		const idsToFilterBy: string[] = [];
		const pageSectionsToExclude: string[] = ['PERSONALIA'];
		const articleSectionsToExclude: string[] = ['Etterbørs'];
		const editionLayouts = await loadEditionLayouts(this.editionData._id);

		if (editionLayouts.status !== 200) {
			throw new LoadResourceError('Error loading edition layouts', editionLayouts);
		}

		const loadedArticleObjects = await Promise.all(
			articleObjects
				.filter((object: PageObject) => {
					const item = this.editionData.items[object.itemId];
					if (articleSectionsToExclude.includes(item.data.section || '')) {
						return false;
					}

					return (
						idsToFilterBy.length === 0 ||
						idsToFilterBy.includes(this.editionData.items[object.itemId].sourceId)
					);
				})
				.filter((object: PageObject) => {
					const section = this.editionData.pages[object.pages[0]].properties?.section;
					if (!section) {
						return true;
					}

					return pageSectionsToExclude.length === 0 || !pageSectionsToExclude.includes(section);
				})
				.map(async (object: PageObject) => {
					const layoutHtml = editionLayouts.body[object._id];
					return {
						object,
						layoutHtml
					};
				})
		);

		const articles = loadedArticleObjects.reduce((acc: Article[], objectWithLayout: PageObjectWithLayout) => {
			const article = createArticleFromLayout(
				objectWithLayout,
				this.editionData,
				this.adapter,
				acc,
				store,
				this.articleIdPrefix
			);
			if (article) {
				acc.push(article);
			}
			return acc;
		}, []);

		articles.sort((a: Article, b: Article) => (a.sortOrder as number) - (b.sortOrder as number));

		this.articles = articles.reduce((acc: Article[], article: Article, index: number, collection: Article[]) => {
			const previous = acc.at(-1);
			if (!previous) {
				acc.push(article);
				return acc;
			}

			if (previous.isArticleGroup && article.size <= 11) {
				previous.addToGroup(article);
				return acc;
			}

			// TODO: Consider trying to gather all notices from same section
			if (article.size <= 11 && collection[index + 1]?.size <= 11) {
				article.groupBy = '';
				article.makeGroup();
			}

			acc.push(article);
			return acc;
		}, []);

		return this.articles;
	}

	getPreviousEditionUrl() {
		if (!this.latestEditions?.length) {
			return '';
		}

		const currentEditionIndex = this.latestEditions.findIndex(
			(edition) => edition.publishDate === this.publishDate
		);
		if (this.latestEditions[currentEditionIndex - 1]) {
			return `/reader/${this.latestEditions[currentEditionIndex - 1]._id}`;
		}

		return '';
	}

	getNextEditionUrl() {
		if (!this.latestEditions?.length) {
			return '';
		}

		const currentEditionIndex = this.latestEditions.findIndex(
			(edition) => edition.publishDate === this.publishDate
		);
		if (this.latestEditions[currentEditionIndex + 1]) {
			return `/reader/${this.latestEditions[currentEditionIndex + 1]._id}`;
		}

		return '';
	}

	getLatestEditionPath(): string {
		const latestEdition = this.latestEditions?.at(-1);
		if (latestEdition) {
			return `/reader/${latestEdition._id}`;
		}

		return this.getPath();
	}

	loadRelatedArticles(_article: Article) {
		return Promise.resolve([]);
	}

	getPath(): string {
		return `/reader/${this.id}`;
	}
}
